// src/auth/session.ts
/**
 * جلسة الدخول: رمز الوصول (15 دقيقة) في الذاكرة مع نسخة في sessionStorage (تبويب واحد)، ورمز التجديد
 * معه. التجديد استباقي قبل الانتهاء بدقيقة، وبتدوير: كل تجديد يستبدل الرمزين. أي فشل في التجديد = خروج.
 */
const KEY = "adman.session";
// رابط الباك إند ثابت في الكود مباشرة — نفس القيمة والمنطق في src/api/client.ts بالضبط، بلا أي اعتماد
// على متغيّر بيئة. لازم يفضل مطابقاً لملف client.ts لو اتعدّل هناك.
const API_BASE = "https://api.adman-sa.com";
const REFRESH_LEAD_MS = 60_000;
const MAX_TIMER = 2_000_000_000;

type Stored = { token: string; exp: number; refresh?: string };
type Listener = () => void;

function decodeExp(token: string): number {
  try {
    const payload = JSON.parse(atob(token.split(".")[1].replace(/-/g, "+").replace(/_/g, "/")));
    return typeof payload.exp === "number" ? payload.exp : 0;
  } catch {
    return 0;
  }
}

function read(): Stored | null {
  try {
    const raw = sessionStorage.getItem(KEY);
    if (!raw) return null;
    const s = JSON.parse(raw) as Stored;
    return s.exp * 1000 > Date.now() || s.refresh ? s : null; // منتهي الوصول لكن معه رمز تجديد: يُجدَّد عند الحاجة
  } catch {
    return null;
  }
}

let current: Stored | null = read();
let timer: ReturnType<typeof setTimeout> | undefined;
let inflight: Promise<boolean> | null = null;
const listeners = new Set<Listener>();

const emit = () => listeners.forEach((l) => l());
const valid = () => !!current && current.exp * 1000 > Date.now();

function persist() {
  if (current) sessionStorage.setItem(KEY, JSON.stringify(current));
  else sessionStorage.removeItem(KEY);
}

function schedule() {
  clearTimeout(timer);
  if (!current) return;
  const left = current.exp * 1000 - Date.now();
  const wait = current.refresh ? left - REFRESH_LEAD_MS : left;
  timer = setTimeout(() => {
    if (wait > MAX_TIMER) return schedule();
    if (current?.refresh) void session.refresh();
    else session.clear();
  }, Math.min(Math.max(0, wait), MAX_TIMER));
}

export const session = {
  token: (): string | null => (valid() ? current!.token : null),
  refreshToken: (): string | null => current?.refresh ?? null,
  expiresAt: (): number | null => (current ? current.exp * 1000 : null),
  isValid: valid,
  set(token: string, refresh?: string) {
    current = { token, exp: decodeExp(token), refresh };
    persist();
    schedule();
    emit();
  },
  clear() {
    if (!current) return;
    current = null;
    persist();
    clearTimeout(timer);
    emit();
  },
  /** يجدّد الرمزين بتدوير رمز التجديد. طلبات التجديد المتزامنة تشترك في طلب واحد. */
  refresh(): Promise<boolean> {
    if (inflight) return inflight;
    const refresh = current?.refresh;
    if (!refresh) return Promise.resolve(false);
    inflight = (async () => {
      try {
        const res = await fetch(`${API_BASE}/api/auth/refresh`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ refresh_token: refresh }) });
        if (!res.ok) {
          session.clear();
          return false;
        }
        const t = (await res.json()) as { access_token: string; refresh_token: string };
        session.set(t.access_token, t.refresh_token);
        return true;
      } catch {
        return false; // انقطاع شبكة: نُبقي الجلسة ونعيد المحاولة عند الطلب التالي
      } finally {
        inflight = null;
      }
    })();
    return inflight;
  },
  /** للحراسة عند فتح الصفحة: جلسة صالحة، أو تجديدها لو انتهى الوصول وبقي رمز التجديد. */
  async ensure(): Promise<boolean> {
    if (valid()) return true;
    return current?.refresh ? session.refresh() : false;
  },
  /** خروج: يبطل رمز التجديد في الخادم (على أفضل جهد) ثم يمسح الجلسة محلياً. */
  async signOut() {
    const refresh = current?.refresh;
    session.clear();
    if (refresh) {
      try {
        await fetch(`${API_BASE}/api/auth/logout`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ refresh_token: refresh }), keepalive: true });
      } catch {
        /* الخروج المحلي تم */
      }
    }
  },
  subscribe(l: Listener): () => void {
    listeners.add(l);
    return () => listeners.delete(l);
  },
};

schedule();
